<script lang="ts">
  import { api } from '../../lib/api/client';
  import type { SearchResult, SearchResults } from '../../lib/api/types';
  import { clock } from '../../lib/utils/time';
  import { t, time } from '../../lib/i18n/index.ts';
  import { categoryIcon, countryName, dayShort, distance, modeIcon, modeLabel, placeName } from './format';

  // Search lists what the member's timeline holds for a text: stays by
  // their place, trips by their way of travel, and notes, newest first, 100
  // at a time (spec 005 FR-049). Each opens its day with the entry
  // picked.
  let { query, onDay }: { query: string; onDay: (date: string, entry?: string) => void } = $props();

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let results = $state<SearchResult[]>([]);
  let more = $state(false);
  let loading = $state(false);
  let done = $state(false);
  let error = $state('');

  $effect(() => {
    const q = query;
    results = [];
    more = false;
    done = false;
    fetchPage(q, '');
  });

  async function fetchPage(q: string, before: string) {
    loading = true;
    error = '';
    try {
      const params = new URLSearchParams({ q, tz });
      if (before) params.set('before', before);
      const got = await api.get<SearchResults>(`/me/timeline/search?${params}`);
      if (q !== query) return;
      results = [...results, ...got.results];
      more = got.more;
      done = true;
    } catch (e: any) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  function where(r: SearchResult): string {
    const p = r.place;
    if (!p) return '';
    return [p.name ? p.address : '', p.city, p.region, p.country_code ? countryName(p.country_code, p.country) : p.country].filter(Boolean).join(', ');
  }

  // when is a result's day and, for a stay or trip, its time in its own zone.
  function when(r: SearchResult): string {
    if (r.kind === 'note') return dayShort(r.date);
    return t('timelineDayTime', { day: dayShort(r.date), time: r.tz ? time(new Date(r.start), r.tz) : clock(r.start) });
  }
</script>

<div class="flex-1 overflow-y-auto p-4 sm:p-6">
  <div class="max-w-3xl mx-auto space-y-4">
    <h2 class="font-semibold" aria-live="polite">
      {#if done}{more ? t('timelineSearchResultsMore', { n: results.length, query }) : t('timelineSearchResults', { n: results.length, query })}{:else}{t('timelineSearching', { query })}{/if}
    </h2>
    {#if error}
      <p class="text-sm" style="color: var(--theme-error);">{error}</p>
    {/if}
    {#if done && results.length === 0}
      <p class="text-sm text-text-muted">{t('timelineSearchNone')}</p>
    {:else if results.length}
      <ul class="card divide-y" style="border-color: var(--theme-surface1);">
        {#each results as r, i (i)}
          <li>
            <button class="w-full text-left px-4 py-3 flex items-start gap-3 hover:opacity-80" onclick={() => onDay(r.date, r.entry_id)}>
              {#if r.kind === 'stay'}
                <i aria-hidden="true" class="las {categoryIcon(r.place?.category)} text-xl" style="color: var(--theme-green);"></i>
              {:else if r.kind === 'trip'}
                <i aria-hidden="true" class="las {modeIcon(r.mode ?? 'other')} text-xl" style="color: var(--theme-blue);"></i>
              {:else}
                <i aria-hidden="true" class="las la-sticky-note text-xl"></i>
              {/if}
              <span class="flex-1 min-w-0">
                <span class="block font-medium truncate">
                  {#if r.kind === 'stay'}{placeName(r.place ?? {})}{:else if r.kind === 'trip'}{r.distance_m ? t('timelineModeDistance', { mode: modeLabel(r.mode ?? 'other'), distance: distance(r.distance_m) }) : modeLabel(r.mode ?? 'other')}{:else}{t('timelineNoteOnDay')}{/if}
                </span>
                {#if where(r)}<span class="block text-xs text-text-muted truncate">{where(r)}</span>{/if}
                {#if r.note}<span class="block text-sm mt-0.5 line-clamp-2">{#if r.kind !== 'note'}<i aria-hidden="true" class="las la-sticky-note mr-1 text-text-muted"></i><span class="sr-only">{`${t('timelineNoteSr')} `}</span>{/if}{r.note}</span>{/if}
                <span class="block text-xs text-text-muted">{when(r)}</span>
              </span>
              <i aria-hidden="true" class="las la-angle-right text-text-muted self-center"></i>
            </button>
          </li>
        {/each}
      </ul>
      {#if more}
        <button class="btn btn-secondary" disabled={loading} onclick={() => fetchPage(query, results[results.length - 1].start)}>{loading ? t('timelineLoading') : t('timelineLoadMore')}</button>
      {/if}
    {/if}
  </div>
</div>
